'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["pathname"];
import getLang from 'I18n/utils/getLang';
import unescapedText from 'I18n/utils/unescapedText';
import UIFieldset from 'UIComponents/form/UIFieldset';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import { parse as parseParams } from 'hub-http/helpers/params';
import Immutable from 'immutable';
import { APP_CERTIFICATION_CREDENTIAL_ID } from 'marketplace-ui-apps-core/common/constants/Certification';
import { getHasAppCertificationCredential } from 'marketplace-ui-apps-core/common/utils/Certification';
import { useCollections } from 'marketplace-ui-common/collections/hooks/useCollections';
import PlanCompatibilityFilter from 'marketplace-ui-common/filters/components/PlanCompatibilityFilter';
import { usePrevious } from 'marketplace-ui-common/hooks/usePrevious';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { useScrollToTop } from 'marketplace-ui-common/hooks/useScrollToTop';
import MainSection from 'marketplace-ui-common/layout/components/MainSection';
import MainSectionInner from 'marketplace-ui-common/layout/components/MainSectionInner';
import MediaQuery from 'marketplace-ui-common/layout/components/MediaQuery';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import { isUninitialized } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { useUsageTracker } from 'marketplace-ui-common/tracking/context/UsageTrackerContext';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { setupCsat } from 'wootric-nps/csat/csat-setup';
import * as GroupTagsActions from '../actions/GroupTagActions';
import Header from '../components/storefront/Header';
import { MobileBrowseBar } from '../components/storefront/mobile/MobileBrowseBar';
import SidebarSection from '../components/storefront/sidebar/SidebarSection';
import AppLanguagesSelect from '../components/storefront/sidebar/filters/AppLanguagesSelect';
import CategoryFeaturesFilter from '../components/storefront/sidebar/filters/CategoryFeaturesFilter';
import CertifiedToggle from '../components/storefront/sidebar/filters/CertifiedToggle';
import DataSyncDirectionFilter from '../components/storefront/sidebar/filters/DataSyncDirectionFilter';
import HubSpotFeaturesFilter from '../components/storefront/sidebar/filters/HubSpotFeaturesFilter';
import IntegrationsFilter from '../components/storefront/sidebar/filters/IntegrationsFilter';
import PriceFilter from '../components/storefront/sidebar/filters/PriceFilter';
import SupportSourceFilter from '../components/storefront/sidebar/filters/SupportSourceFilter';
import SupportedLanguagesSelect from '../components/storefront/sidebar/filters/SupportedLanguagesSelect';
import CategoryNav from '../components/storefront/sidebar/nav/CategoryNav';
import HSPageViewTracker from '../components/tracking/HSPageViewTracker';
import * as FilterKey from '../constants/search/FilterKey';
import { useAppContext } from '../context/AppContext.tsx';
import StorefrontFiltersContext from '../context/StorefrontFiltersContext';
import StorefrontUsageTrackerContext from '../context/StorefrontUsageTrackerContext';
import StorefrontFilters from '../records/StorefrontFilters';
import { getAppIntegrations } from '../selectors/appIntegrations';
import { getIsCustomer } from '../selectors/auth';
import { getAppIntegrationsRequestState } from '../selectors/requestStates';
import FeaturedCalloutsSection from '../storefront/components/FeaturedCalloutsSection';
import { debug } from '../utils/env';
import { filterFeaturesByCategory } from '../utils/groupTags';
import { useParsedPathParams } from '../utils/hooks/useParsedPathParams';
import useWindowWidth from '../utils/hooks/useWindowWidth';
import { getAllCategoriesUrl, getFeaturedAppsUrl, getHasPathPagination, getListingDetailUrl, getPathPagination, stripPathPagination } from '../utils/urls';
import { getAppRecommendationType } from '../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FEATURED_APPS_REGEX = new RegExp(`^${getFeaturedAppsUrl()}`);
const ALL_CATEGORIES_REGEX = new RegExp(`^${getAllCategoriesUrl()}?/(?:page/[0-9]+)?$`);
const Storefront = ({
  children
}) => {
  const params = useParams();
  const history = useHistory();
  const {
    search,
    pathname
  } = useLocation();
  const language = getLang();
  const {
    collection: collectionSlug,
    collectionId: previewCollectionId
  } = params;
  const appIntegrations = useSelector(getAppIntegrations);
  const appIntegrationsRequestState = useSelector(getAppIntegrationsRequestState);
  const isCustomer = useSelector(getIsCustomer);
  const appContext = useAppContext();
  const isAdminPreview = appContext === 'admin-preview';
  const {
    getCollectionBySlug,
    getCollectionById
  } = useCollections({
    appContext,
    offeringType: 'APP'
  });
  useScrollToTop(pathname);
  useEffect(() => {
    if (isAdminPreview) return null;
    const timer = setTimeout(() => {
      setupCsat('ECOSYSTEM_APP_MARKETPLACE');
    }, 5000);
    return () => clearTimeout(timer);
  }, [isAdminPreview]);
  const collection = isAdminPreview ? getCollectionById(parseInt(previewCollectionId, 10)) : getCollectionBySlug(collectionSlug);
  const collectionId = collection ? collection.id : 0;
  const collectionListing = collection && (isAdminPreview ? collection.draftListing : collection.publishedListing);
  const dispatch = useDispatch();
  const fetchAppIntegrations = useCallback(() => dispatch(GroupTagsActions.fetchAppIntegrations()), [dispatch]);
  const {
    category: activeCategory,
    superCategory: activeSuperCategory,
    page: activePage = 1
  } = useParsedPathParams(params);
  const isFeaturedApps = FEATURED_APPS_REGEX.test(pathname);
  const isAllCategories = ALL_CATEGORIES_REGEX.test(pathname);
  const queryString = search.slice(1);
  const {
    source
  } = parseParams(queryString);
  const prevActiveCategory = usePrevious(activeCategory);
  const prevActiveSuperCategory = usePrevious(activeSuperCategory);
  const filters = useMemo(() => {
    return new StorefrontFilters().withMutations(mutableFilters => {
      mutableFilters.set(FilterKey.CATEGORY, activeCategory);
      mutableFilters.set(FilterKey.SUPER_CATEGORY, activeSuperCategory);
      mutableFilters.fromQueryString(queryString);
    });
  }, [activeCategory, activeSuperCategory, queryString]);
  const {
    [FilterKey.CATEGORY_FEATURES]: categoryFeaturesValue,
    [FilterKey.PRICING]: planTypeValue,
    [FilterKey.PLAN_COMPATIBLE]: planCompatibleValue,
    [FilterKey.TIERS]: tiersValue,
    [FilterKey.HUBSPOT_FEATURES]: hubspotFeaturesValue,
    [FilterKey.INTEGRATIONS]: integrationsValue,
    [FilterKey.CREDENTIALS]: credentialsValue,
    [FilterKey.SYNC]: syncValue,
    [FilterKey.APP_LANGUAGES]: appLanguagesValue,
    [FilterKey.SUPPORT_LANGUAGES]: supportedLanguagesValue,
    [FilterKey.SUPPORT_SOURCE]: supportSourceValue,
    [FilterKey.SEARCH]: searchValue
  } = filters;
  const usageTracker = useUsageTracker();
  const usageEventProperties = useMemo(() => {
    const properties = Object.assign({
      screen: 'storefront',
      preferredLanguage: language
    }, filters.toEventProperties());
    if (isAllCategories) {
      properties.category = 'all categories';
    } else if (isFeaturedApps) {
      properties.category = 'featured apps';
    } else if (collectionListing) {
      const {
        id: collectionListingId,
        name: internalName
      } = collectionListing;
      properties.category = `collection::${collectionId}_${collectionListingId}`;
      properties.name = internalName;
    }
    return properties;
  }, [collectionId, collectionListing, filters, isAllCategories, isFeaturedApps, language]);
  const trackInteraction = useCallback((action, properties = {}, name = 'storefront-interaction', _usageTracker = usageTracker) => {
    _usageTracker.track(name, Object.assign({}, usageEventProperties, properties, {
      action
    }));
  }, [usageEventProperties, usageTracker]);
  const trackFilterChange = useCallback((filter, _value) => {
    let value = _value;
    let eventName = 'storefront-change-filter';
    if (Immutable.Set.isSet(value)) {
      value = value.toJS().sort();
      eventName += '-set';
    } else if (typeof value === 'boolean') {
      eventName += '-boolean';
    } else if (value == null) {
      value = '';
    }
    trackInteraction('change filter', {
      filter,
      value
    }, eventName);
  }, [trackInteraction]);
  const trackFilterNavItemClick = useCallback((filter, value) => {
    trackInteraction('click filter nav item', {
      filter,
      value
    }, 'storefront-click-filter-nav-item');
  }, [trackInteraction]);
  const trackResultClick = useCallback((result, itemIndex, properties = {}) => {
    const eventProperties = Object.assign({}, properties, {
      value: result.slug,
      itemIndex,
      recommendationType: getAppRecommendationType(result)
    });
    trackInteraction('click result item', eventProperties, 'storefront-click-result-item');
  }, [trackInteraction]);
  const trackViewEvent = useCallback((eventName, properties = {}) => {
    usageTracker.track(eventName, Object.assign({}, usageEventProperties, properties));
  }, [usageEventProperties, usageTracker]);
  const trackPageView = useCallback(properties => trackViewEvent('storefront-pageview', Object.assign({}, properties, {
    source
  })), [source, trackViewEvent]);
  const trackFilterView = useCallback(properties => trackViewEvent('storefront-filter', properties), [trackViewEvent]);
  const pushFilters = useCallback((updatedFilters, categoryChange = false) => {
    if (!filters.equals(updatedFilters) || categoryChange) {
      history.push(updatedFilters.toLocation(categoryChange ? null : collectionSlug));
    }
  }, [collectionSlug, filters, history]);
  const replaceFilters = useCallback(updatedFilters => {
    if (!filters.equals(updatedFilters)) {
      history.replace(updatedFilters.toLocation(collectionSlug));
    }
  }, [collectionSlug, filters, history]);
  const handleFilterChange = useCallback((key, value) => {
    const updatedFilters = filters.set(key, value);
    pushFilters(updatedFilters);
    trackFilterChange(key, value);
  }, [filters, pushFilters, trackFilterChange]);
  const handlePageChange = useCallback((page, _filters) => {
    const _toLocation = (_filters || filters).toLocation(collectionSlug),
      {
        pathname: newPathname
      } = _toLocation,
      restLocation = _objectWithoutPropertiesLoose(_toLocation, _excluded);
    history.push(Object.assign({}, restLocation, {
      pathname: getPathPagination(newPathname, page)
    }));
    window.scrollTo(0, 0);
  }, [collectionSlug, filters, history]);
  const handleSortChange = useCallback((field, order) => {
    const updatedFilters = filters.withMutations(mutableFilters => {
      mutableFilters.set(FilterKey.SORT_FIELD, field).set(FilterKey.SORT_ORDER, order);
    });
    pushFilters(updatedFilters);
  }, [filters, pushFilters]);
  const updateChangedFilters = useCallback(newFilters => {
    const changedFilters = filters.mergeWith((oldVal, newVal, key) => {
      if (oldVal !== newVal) {
        trackFilterChange(key, newVal);
        return newVal;
      }
      return oldVal;
    }, newFilters);
    pushFilters(changedFilters);
  }, [filters, pushFilters, trackFilterChange]);
  const handleClearFilters = useCallback(() => {
    pushFilters(filters.clear());
  }, [filters, pushFilters]);
  const handleCategoryChange = useCallback((superCategory, category) => {
    const updatedFilters = filters.withMutations(mutableFilters => {
      mutableFilters.set(FilterKey.SUPER_CATEGORY, superCategory).set(FilterKey.CATEGORY, category).delete(FilterKey.SEARCH);
      if (category !== activeCategory) {
        mutableFilters.delete(FilterKey.CATEGORY_FEATURES);
      }
    });
    pushFilters(updatedFilters, true);
  }, [activeCategory, filters, pushFilters]);
  const handleSearchAppSelect = useCallback((slug, category, superCategory) => {
    history.push(getListingDetailUrl(slug, category, superCategory));
  }, [history]);
  const handleSearchFeatureSelect = useCallback((superCategory, category, feature) => {
    const updatedFilters = filters.withMutations(mutableFilters => {
      mutableFilters.set(FilterKey.SUPER_CATEGORY, superCategory).set(FilterKey.CATEGORY, category).set(FilterKey.CATEGORY_FEATURES, category === activeCategory ? categoryFeaturesValue.add(feature) : categoryFeaturesValue.clear().add(feature));
    });
    pushFilters(updatedFilters);
  }, [activeCategory, categoryFeaturesValue, filters, pushFilters]);
  const handleSearchFilterSelect = useCallback((filterKey, filterValue) => {
    const updatedFilters = filters.set(filterKey, filters.get(filterKey).add(filterValue));
    pushFilters(updatedFilters);
  }, [filters, pushFilters]);
  const handleSearchSubmit = useCallback(searchFilters => {
    history.push(searchFilters.toLocation());
  }, [history]);
  const handleSearchClear = useCallback(() => {
    const updatedFilters = filters.delete(FilterKey.SEARCH);
    pushFilters(updatedFilters);
  }, [filters, pushFilters]);
  const openFilters = useWindowWidth({
    sm: false,
    md: true
  });
  useEffect(() => {
    if (isUninitialized(appIntegrationsRequestState) && !isAdminPreview) {
      fetchAppIntegrations();
    }
  }, [appIntegrationsRequestState, fetchAppIntegrations, isAdminPreview]);
  useEffect(() => {
    if (getHasPathPagination(pathname) && activePage === 1) {
      history.replace({
        pathname: stripPathPagination(pathname),
        search
      });
    }
  }, [activePage, history, pathname, search]);
  useEffect(() => {
    if (activeCategory !== prevActiveCategory || activeSuperCategory !== prevActiveSuperCategory) {
      const updatedFilters = filters.withMutations(mutableFilters => {
        mutableFilters.set(FilterKey.CATEGORY_FEATURES, activeCategory ? filterFeaturesByCategory(mutableFilters.get(FilterKey.CATEGORY_FEATURES), activeCategory) : categoryFeaturesValue.clear());
      });
      replaceFilters(updatedFilters);
    }
  }, [activeCategory, activeSuperCategory, filters, prevActiveCategory, prevActiveSuperCategory, replaceFilters, categoryFeaturesValue]);
  useEffect(() => {
    if (debug('storefront-filters')) {
      console.groupCollapsed('storefront filters');
      console.log('filters:\n', filters.toJS());
      console.log('query:\n', filters.toQuery());
      console.log('search query:\n', filters.toSearchQuery());
      console.log('query string:\n', filters.toQueryString());
      console.groupEnd();
    }
  }, [filters]);
  const disableFilters = isAdminPreview;
  const {
    paddingClassNames
  } = useResponsiveProps({
    paddingClassNames: {
      xs: 'p-bottom-5 p-top-0',
      md: "p-y-10 p-x-12"
    }
  });
  return /*#__PURE__*/_jsx(StorefrontFiltersContext.Provider, {
    value: {
      filters,
      isAllCategories,
      clearFilters: handleClearFilters,
      onPageChange: handlePageChange,
      onSortChange: handleSortChange,
      pushFilters,
      updateFilter: handleFilterChange,
      updateAllFilters: updateChangedFilters
    },
    children: /*#__PURE__*/_jsxs(StorefrontUsageTrackerContext.Provider, {
      value: {
        trackInteraction,
        trackPageView,
        trackFilterView,
        trackResultClick
      },
      children: [/*#__PURE__*/_jsx(HSPageViewTracker, {
        pathname: pathname
      }), /*#__PURE__*/_jsx(Header, {
        activeCategory: activeCategory,
        activeSuperCategory: activeSuperCategory,
        appIntegrations: appIntegrations,
        headingLevel: "h2",
        isAdminPreview: isAdminPreview,
        isCustomer: isCustomer,
        onSearchAppSelect: handleSearchAppSelect,
        onSearchCategorySelect: handleCategoryChange,
        onSearchClear: handleSearchClear,
        onSearchFeatureSelect: handleSearchFeatureSelect,
        onSearchFilterSelect: handleSearchFilterSelect,
        onSearchSubmit: handleSearchSubmit,
        searchValue: searchValue,
        trackInteraction: trackInteraction
      }), /*#__PURE__*/_jsx(MediaQuery, {
        maxScreenSize: ScreenSizes.MD,
        children: !isFeaturedApps && /*#__PURE__*/_jsx(MobileBrowseBar, {
          className: isAllCategories ? 'm-bottom-5' : undefined,
          collection: collection,
          filters: filters,
          isAllCategories: isAllCategories
        })
      }), /*#__PURE__*/_jsx(MainSection, {
        className: isFeaturedApps ? 'p-x-0' : paddingClassNames,
        "data-test-id": "storefront",
        responsive: true,
        children: /*#__PURE__*/_jsx(MainSectionInner, {
          children: /*#__PURE__*/_jsxs(UIGrid, {
            children: [/*#__PURE__*/_jsx(MediaQuery, {
              minScreenSize: ScreenSizes.MD,
              children: !isFeaturedApps && /*#__PURE__*/_jsx(UIGridItem, {
                size: {
                  xs: 12,
                  md: 3
                },
                children: /*#__PURE__*/_jsx("aside", {
                  children: /*#__PURE__*/_jsxs(UIFieldset, {
                    disabled: disableFilters,
                    _size: "small",
                    children: [/*#__PURE__*/_jsx(SidebarSection, {
                      defaultOpen: openFilters,
                      title: unescapedText('appMarketplace.storefront.filters.category.titleText'),
                      children: /*#__PURE__*/_jsx(CategoryNav, {
                        activeCategory: activeCategory,
                        activeSuperCategory: activeSuperCategory,
                        isAdminPreview: isAdminPreview,
                        isAllCategories: isAllCategories,
                        onCategoryClick: (superCategory, category) => handleCategoryChange(superCategory, category),
                        trackFilterNavItemClick: trackFilterNavItemClick
                      })
                    }), activeCategory && /*#__PURE__*/_jsx(SidebarSection, {
                      defaultOpen: openFilters,
                      title: unescapedText('appMarketplace.storefront.filters.categoryFeatures.titleText'),
                      tooltip: unescapedText('appMarketplace.storefront.filters.categoryFeatures.sectionTooltip'),
                      children: /*#__PURE__*/_jsx(CategoryFeaturesFilter, {
                        activeCategory: activeCategory,
                        onChange: event => handleFilterChange(FilterKey.CATEGORY_FEATURES, Immutable.Set(event.target.value)),
                        value: categoryFeaturesValue.toArray()
                      })
                    }), /*#__PURE__*/_jsx(SidebarSection, {
                      defaultOpen: openFilters,
                      title: unescapedText('appMarketplace.storefront.filters.planType.titleText'),
                      tooltip: unescapedText('appMarketplace.storefront.filters.planType.sectionTooltip'),
                      children: /*#__PURE__*/_jsx(PriceFilter, {
                        onChange: event => {
                          handleFilterChange(FilterKey.PRICING, event.target.value);
                        },
                        value: planTypeValue
                      })
                    }), /*#__PURE__*/_jsx(SidebarSection, {
                      defaultOpen: !!planCompatibleValue || !tiersValue.isEmpty(),
                      title: unescapedText('appMarketplace.storefront.filters.compatibility.titleText'),
                      tooltip: unescapedText('appMarketplace.storefront.filters.compatibility.sectionTooltip'),
                      children: /*#__PURE__*/_jsx(PlanCompatibilityFilter, {
                        appContext: isCustomer ? 'app' : 'public',
                        ariaLabel: unescapedText('appMarketplace.storefront.filters.compatibility.titleText'),
                        onChange: event => {
                          const {
                            portalCompatible,
                            tiers
                          } = event.target.value;
                          handleFilterChange(FilterKey.TIERS, Immutable.Set(tiers));
                          handleFilterChange(FilterKey.PLAN_COMPATIBLE, portalCompatible);
                        },
                        value: {
                          portalCompatible: planCompatibleValue,
                          tiers: tiersValue.toArray()
                        }
                      })
                    }), /*#__PURE__*/_jsx(SidebarSection, {
                      defaultOpen: !hubspotFeaturesValue.isEmpty(),
                      title: unescapedText('appMarketplace.storefront.filters.hubspotFeatures.titleText'),
                      tooltip: unescapedText('appMarketplace.storefront.filters.hubspotFeatures.sectionTooltip'),
                      children: /*#__PURE__*/_jsx(HubSpotFeaturesFilter, {
                        onChange: event => handleFilterChange(FilterKey.HUBSPOT_FEATURES, Immutable.Set(event.target.value)),
                        trackInteraction: trackInteraction,
                        value: hubspotFeaturesValue.toArray()
                      })
                    }), /*#__PURE__*/_jsx(SidebarSection, {
                      defaultOpen: !integrationsValue.isEmpty(),
                      title: unescapedText('appMarketplace.storefront.filters.integrations.titleText'),
                      tooltip: unescapedText('appMarketplace.storefront.filters.integrations.sectionTooltip'),
                      children: /*#__PURE__*/_jsx(IntegrationsFilter, {
                        onChange: event => handleFilterChange(FilterKey.INTEGRATIONS, Immutable.Set(event.target.value)),
                        trackInteraction: trackInteraction,
                        value: integrationsValue.toArray()
                      })
                    }), /*#__PURE__*/_jsx(SidebarSection, {
                      defaultOpen: getHasAppCertificationCredential(credentialsValue),
                      title: unescapedText('appMarketplace.storefront.filters.certified.titleText'),
                      tooltip: unescapedText('appMarketplace.storefront.filters.certified.sectionTooltip'),
                      children: /*#__PURE__*/_jsx(CertifiedToggle, {
                        onChange: value => {
                          handleFilterChange(FilterKey.CREDENTIALS, value ? [APP_CERTIFICATION_CREDENTIAL_ID] : []);
                        },
                        value: getHasAppCertificationCredential(credentialsValue)
                      })
                    }), /*#__PURE__*/_jsx(SidebarSection, {
                      defaultOpen: !!syncValue,
                      title: unescapedText('appMarketplace.storefront.filters.sync.titleText'),
                      tooltip: unescapedText('appMarketplace.storefront.filters.sync.sectionTooltip'),
                      children: /*#__PURE__*/_jsx(DataSyncDirectionFilter, {
                        onChange: event => handleFilterChange(FilterKey.SYNC, event.target.value),
                        value: syncValue
                      })
                    }), /*#__PURE__*/_jsx(SidebarSection, {
                      defaultOpen: !appLanguagesValue.isEmpty(),
                      title: unescapedText('appMarketplace.storefront.filters.appLanguages.titleText'),
                      tooltip: unescapedText('appMarketplace.storefront.filters.appLanguages.sectionTooltip'),
                      children: /*#__PURE__*/_jsx(AppLanguagesSelect, {
                        onChange: value => handleFilterChange(FilterKey.APP_LANGUAGES, value),
                        value: appLanguagesValue
                      })
                    }), /*#__PURE__*/_jsx(SidebarSection, {
                      defaultOpen: !supportedLanguagesValue.isEmpty(),
                      title: unescapedText('appMarketplace.storefront.filters.supportedLanguages.titleText'),
                      tooltip: unescapedText('appMarketplace.storefront.filters.supportedLanguages.sectionTooltip'),
                      children: /*#__PURE__*/_jsx(SupportedLanguagesSelect, {
                        onChange: value => handleFilterChange(FilterKey.SUPPORT_LANGUAGES, value),
                        value: supportedLanguagesValue
                      })
                    }), /*#__PURE__*/_jsx(SidebarSection, {
                      defaultOpen: !!supportSourceValue,
                      title: unescapedText('appMarketplace.storefront.filters.supportSource.titleText'),
                      tooltip: unescapedText('appMarketplace.storefront.filters.supportSource.sectionTooltip'),
                      children: /*#__PURE__*/_jsx(SupportSourceFilter, {
                        onChange: event => {
                          handleFilterChange(FilterKey.SUPPORT_SOURCE, event.target.value);
                        },
                        value: supportSourceValue
                      })
                    })]
                  })
                })
              })
            }), /*#__PURE__*/_jsx(UIGridItem, {
              size: {
                xs: 12,
                md: isFeaturedApps ? 12 : 9
              },
              children: /*#__PURE__*/_jsxs("main", {
                children: [isAllCategories && /*#__PURE__*/_jsx(FeaturedCalloutsSection, {}), children]
              })
            })]
          })
        })
      })]
    })
  });
};
Storefront.propTypes = {
  children: PropTypes.node
};
export default Storefront;